import React, { useState } from 'react';
import axios from 'axios';
import { Tooltip } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { Download } from 'assets/svg';
import { sendNotification, triggerBlobDownload } from 'utils/helperMethods';

type Method = 'get' | 'post';

interface Props {
    url: string;
    method?: Method;
    payload?: any;
    fileName?: string;
    disabled?: boolean;
    tooltip?: string;
    disabledTooltip?: string;
    label?: string;
    className?: string;
    iconClassName?: string;
    icon?: React.ReactNode;
    infoMessage?: string;
    successMessage?: string;
    errorMessage?: string;
    mixpanelEvent?: () => void;
    onSuccess?: (blob: Blob) => void;
    onError?: (error: any) => void;
}

// Standard Content-Disposition filename matcher. Handles plain
// `filename="foo.xlsx"` and the RFC 5987 `filename*=UTF-8''foo.xlsx` forms,
// with or without surrounding quotes.
const FILENAME_RE = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i;

const DownloadFileButton: React.FC<Props> = ({
    url,
    method = 'get',
    payload,
    fileName,
    disabled = false,
    tooltip,
    disabledTooltip,
    label,
    className,
    iconClassName,
    icon,
    infoMessage = 'Your download is being prepared...',
    successMessage = 'Download Completed.',
    errorMessage = 'Failed to download. Please try again.',
    mixpanelEvent,
    onSuccess,
    onError,
}) => {
    const [loading, setLoading] = useState(false);
    const isDisabled = disabled || loading;

    const handleClick = async () => {
        if (isDisabled) return;
        if (!url) return;
        mixpanelEvent?.();
        setLoading(true);
        if (infoMessage) sendNotification('info', infoMessage);

        try {
            const response =
                method === 'post'
                    ? await axios.post(url, payload, { responseType: 'blob' })
                    : await axios.get(url, { responseType: 'blob' });

            const cd = response.headers?.['content-disposition'] ?? '';
            const match = FILENAME_RE.exec(cd);
            const resolvedName = match?.[1] ? decodeURIComponent(match[1]) : fileName ?? 'download';

            triggerBlobDownload(response.data, resolvedName);

            if (successMessage) sendNotification('success', successMessage);
            onSuccess?.(response.data);
        } catch (e: any) {
            sendNotification('error', e?.response?.data?.message ?? errorMessage);
            onError?.(e);
        } finally {
            setLoading(false);
        }
    };

    const wrapperCls = `flex items-center gap-1 ${
        isDisabled ? 'cursor-not-allowed text-gray-300' : 'cursor-pointer text-violetAlt'
    } ${className ?? ''}`;

    const trigger = (
        <span onClick={handleClick} className={wrapperCls}>
            <span className={iconClassName}>
                {loading ? <LoadingOutlined spin style={{ fontSize: 14 }} /> : icon ?? <Download />}
            </span>
            {label ? <span>{label}</span> : null}
        </span>
    );

    const titleText = isDisabled ? disabledTooltip : tooltip;
    if (!titleText) return trigger;
    return (
        <Tooltip title={titleText} placement="top" destroyTooltipOnHide overlayInnerStyle={{ fontSize: 11 }}>
            {trigger}
        </Tooltip>
    );
};

export default DownloadFileButton;
